import { useCallback, useRef } from 'react';

import { usePostLeadDataMutation } from '@/_generic/actions/apis';
import {
  type TCouponModelData,
  checkoutStateModel,
  investmentStateModel,
  type TGlobalDataModel,
  type TGlobalDataModelPaymentOptions,
  type TGlobalDataModelCheckout,
  type TGlobalDataModelInvestment,
  type TGlobalDataModelAuditLog,
  paymentOptionsStateModel,
} from '@/_models';
import { useUrlParams } from '@/_navigation';
import { useAppSelector } from '@/_state/hooks';

import { useCourseSelection } from './use-course-selection';
import useDataSaving from './use-global-data-saving';
import useDataValidation from './use-global-data-validation';
import { useInit } from './use-init';
import { useUpdateCache } from './use-update-cache';

const DATA_VERSION = 2;
const defaultCache = {} as TGlobalDataModel;

export const useGlobalData = () => {
  const {
    isDev,
    isReviseMode,
    paramLeadId,
    paramSummaryId,
    paramCampusId,
    resolvedCurrentPath,
    isBridgePlanPage,
    isConsentPage,
  } = useUrlParams();

  const [leadOrderDataApi, leadOrderDataRes] = usePostLeadDataMutation({ fixedCacheKey: 'UPDATE_LEAD_DATA' });

  const { data } = useAppSelector((s) => s.localStorageContainer);
  const actualData = (data || defaultCache) as TGlobalDataModel;
  const leadDataRef = useRef(actualData);
  leadDataRef.current = actualData as TGlobalDataModel;

  const {
    link,
    timeStamp,
    coupons,
    investment,
    paymentOptions,
    checkout: checkoutData,
    persistPaymentOptions,
    is_climb,
    isLocked,
  } = leadDataRef.current;

  const checkout = (checkoutData ?? checkoutStateModel) as TGlobalDataModelCheckout;
  const localInvestment = (investment ?? investmentStateModel) as TGlobalDataModelInvestment;

  const { getCourseSelection } = useCourseSelection(localInvestment);

  const updateCache = useUpdateCache({ ...actualData, isLocked });

  const { overrideGlobalData } = updateCache;

  /** Do not use it outside of this file. except consent page */
  const updateServerRaw = useCallback(
    async (removeCCInfo?: boolean, hasBeenProcessedWithDiscountCheckout = false, data?: TGlobalDataModel) => {
      if (isLocked) return false;

      const { checkout: _checkout, paymentOptions: _paymentOptions } = data || leadDataRef.current;

      let nCheckout = _checkout;
      let nPaymentOptions = paymentOptions;

      if (_checkout && removeCCInfo) {
        nCheckout = { ..._checkout };
        nCheckout.card_1 = { ...checkoutStateModel.card_1 };
        nCheckout.card_2 = { ...checkoutStateModel.card_2 };
        nCheckout.password = checkoutStateModel.password;
      }

      if (_paymentOptions && removeCCInfo) {
        nPaymentOptions = { ..._paymentOptions };
        nPaymentOptions.isPaymentSplitOpen = false;
      }

      const body = {
        ...leadDataRef.current,
        isLocked: !!(
          (nCheckout?.hasReviewedTerms || nCheckout?.termsReviewedByCustomer) &&
          nCheckout?.paymentProcessed &&
          nCheckout?.termsAccepted === 'YES'
        ),
        checkout: nCheckout,
        paymentOptions: nPaymentOptions,
        version: DATA_VERSION,
        hasBeenProcessedWithDiscountCheckout,
      };

      try {
        await leadOrderDataApi({
          leadId: paramLeadId,
          summaryId: paramSummaryId,
          campusId: paramCampusId,
          leadData: { ...body, link: resolvedCurrentPath },
        }).unwrap();
        return true;
      } catch (e) {
        console.error('Error updating server', e);
        return false;
      }
    },
    [isLocked, paymentOptions, leadOrderDataApi, paramLeadId, paramSummaryId, paramCampusId, resolvedCurrentPath]
  );

  const updateServer = useCallback(
    async (removeCCInfo?: boolean, hasBeenProcessedWithDiscountCheckout = false, data?: TGlobalDataModel) => {
      if (isConsentPage || (isReviseMode && !removeCCInfo) || isDev) {
        return undefined;
      }

      const res = await updateServerRaw(removeCCInfo, hasBeenProcessedWithDiscountCheckout, data);
      return res;
    },
    [isConsentPage, isDev, isReviseMode, updateServerRaw]
  );

  useDataValidation({
    doNotCheckDataAnomaly: isConsentPage || isBridgePlanPage,
    leadData: leadDataRef.current,
    overrideGlobalData,
  });

  const { serverUpdateRequired } = useDataSaving({ resolvedCurrentPath, paramLeadId, updateServer });

  const initLeadData = useInit(leadDataRef.current, updateCache);

  return {
    leadLocalData: leadDataRef.current,

    link,
    timeStamp,
    isLocked,
    coupons: coupons ?? ([] as TCouponModelData),
    investment: (investment ?? investmentStateModel) as TGlobalDataModelInvestment,
    persistPaymentOptions,
    paymentOptions: (paymentOptions ?? paymentOptionsStateModel) as TGlobalDataModelPaymentOptions,
    checkout: (checkout ?? checkoutStateModel) as TGlobalDataModelCheckout,
    auditLog: leadDataRef.current.auditLog ?? ({ termsSubSections: {} } as TGlobalDataModelAuditLog),

    initLeadData,
    getCourseSelection,

    updateServer,
    updateServerRaw,
    updateServerAuto: () => {
      serverUpdateRequired.setTrue();
    },

    leadOrderDataRes,
    is_climb,
    ...updateCache,
  };
};

export type TUseGlobalData = ReturnType<typeof useGlobalData>;
